import { RootState } from '../../../app/store';
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { OrderDirection } from '../../Datatable/OrderDirection';
import ContactNameModel from '../../ContactSelector/models/ContactNameModel';
import MarketInfoQueryModel, { MarketInfoType } from '../models/MarketInfoQueryModel';
import MarketInfoModel from '../models/MarketInfoModel';
import { QueryItem } from '../../Tonnage/models/TonnageQueryModel';
import { SearchType } from '../../Tonnage/reducers/TonnageSearchReducer';

interface MarketInfoSearchState {
    query: MarketInfoQueryModel,
    searchResults: MarketInfoModel[],
    triggerSearch: number,
    datatableSelectedIndex: number,
    searchResultTotalRecord: number,
    checkedMarketInfos: MarketInfoModel[],
    searchType: SearchType,
    triggerFocusVesselName: number,
    ownerContactSearchKeyword: string,
    ownerContactNames: ContactNameModel[],
    chartererContactSearchKeyword: string
    chartererContactNames: ContactNameModel[],
    firstSearchAlready: boolean,
    timerId: NodeJS.Timeout | null
}

const initialState: MarketInfoSearchState = {
    query: {
        vesselNames: [],
        owners: [],
        charterers: [],
        marketInfoType: MarketInfoType.All,
        cargoTypes: [],
        loadPortAreas: [],
        disportAreas: [],
        sizeMin: 0,
        sizeMax: 0,
        updatedDateFrom: 0,
        orderBy: "updatedDate",
        orderDirection: OrderDirection.Desc,
    } as MarketInfoQueryModel,
    searchResults: [],
    triggerSearch: 0,
    datatableSelectedIndex: -1,
    searchResultTotalRecord: 0,
    checkedMarketInfos: [],
    searchType: SearchType.Search,
    triggerFocusVesselName: 0,
    ownerContactSearchKeyword: "",
    ownerContactNames: [],
    chartererContactSearchKeyword: "",
    chartererContactNames: [],
    firstSearchAlready: false,
    timerId: null
}

export const MarketInfoSearchSlice = createSlice({
    name: "MarketInfoSearch",
    initialState,
    reducers: {
        setVesselNames: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.vesselNames = action.payload;
        },
        setOwners: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.owners = action.payload;
        },
        setCharterers: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.charterers = action.payload;
        },
        setMarketInfoType: (state, action: PayloadAction<MarketInfoType>) => {
            state.query.marketInfoType = action.payload;
        },
        setCargoTypes: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.cargoTypes = action.payload;
        },
        setLoadPortAreas: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.loadPortAreas = action.payload;
        },
        setDisportAreas: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.disportAreas = action.payload;
        },
        setSizeMin: (state, action: PayloadAction<number>) => {
            state.query.sizeMin = action.payload;
        },
        setSizeMax: (state, action: PayloadAction<number>) => {
            state.query.sizeMax = action.payload;
        },
        setUpdatedDateFrom: (state, action: PayloadAction<number>) => {
            state.query.updatedDateFrom = action.payload;
        },
        setOrderBy: (state, action: PayloadAction<string>) => {
            state.query.orderBy = action.payload;
        },
        setOrderDirection: (state, action: PayloadAction<OrderDirection>) => {
            state.query.orderDirection = action.payload;
        },
        setMarketInfoSearchResults: (state, action: PayloadAction<MarketInfoModel[]>) => {
            state.searchResults = action.payload;
        },
        setTriggerMarketInfoSearch: (state, action: PayloadAction<number>) => {
            state.triggerSearch = action.payload;
        },
        setDatatableSelectedMarketInfoIndex: (state, action: PayloadAction<number>) => {
            state.datatableSelectedIndex = action.payload;
        },
        setSearchResultTotalRecord: (state, action: PayloadAction<number>) => {
            state.searchResultTotalRecord = action.payload;
        },
        setMarketInfoquery: (state, action: PayloadAction<MarketInfoQueryModel>) => {
            state.query = action.payload;
        },
        resetMarketInfoSearch: (state) => {
            state.query = initialState.query;
        },
        setCheckedMarketInfos: (state, action: PayloadAction<MarketInfoModel[]>) => {
            state.checkedMarketInfos = action.payload;
        },
        setSearchType: (state, action: PayloadAction<SearchType>) => {
            state.searchType = action.payload;
        },
        setTriggerFocusVesselName: (state, action: PayloadAction<number>) => {
            state.triggerFocusVesselName = action.payload;
        },
        setOwnerContactSearchKeyword: (state, action: PayloadAction<string>) => {
            state.ownerContactSearchKeyword = action.payload;
        },
        setOwnerContactNames: (state, action: PayloadAction<ContactNameModel[]>) => {
            state.ownerContactNames = action.payload;
        },
        setChartererContactSearchKeyword: (state, action: PayloadAction<string>) => {
            state.chartererContactSearchKeyword = action.payload;
        },
        setChartererContactNames: (state, action: PayloadAction<ContactNameModel[]>) => {
            state.chartererContactNames = action.payload;
        },
        setFirstSearchAlready: (state, action: PayloadAction<boolean>) => {
            state.firstSearchAlready = action.payload;
        },
        setTimerId: (state, action: PayloadAction<NodeJS.Timeout | null>) => {
            state.timerId = action.payload;
        },
    }
});

export const MarketInfoSearchSelector = createSelector(
    (state: RootState) => state.marketInfoSearch,
    (data) => data
);

export const {
    setCharterers,
    setVesselNames,
    setOwners,
    setLoadPortAreas,
    setMarketInfoType,
    setDisportAreas,
    setCargoTypes,
    setSizeMin,
    setSizeMax,
    setUpdatedDateFrom,
    setOrderBy,
    setOrderDirection,
    setMarketInfoSearchResults,
    setTriggerMarketInfoSearch,
    setDatatableSelectedMarketInfoIndex,
    setSearchResultTotalRecord,
    setMarketInfoquery,
    resetMarketInfoSearch,
    setCheckedMarketInfos,
    setSearchType,
    setTriggerFocusVesselName,
    setOwnerContactSearchKeyword,
    setOwnerContactNames,
    setChartererContactSearchKeyword,
    setChartererContactNames,
    setFirstSearchAlready,
    setTimerId
} = MarketInfoSearchSlice.actions;

export default MarketInfoSearchSlice.reducer;